<template>
  <v-dialog v-model="dialogOpen" max-width="600" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center ga-2">
        <v-icon size="20" color="primary">mdi-format-list-checks</v-icon>
        <span class="text-subtitle-1">
          {{ $t("ncrm.integrations.mailjet.manageLists.title") }}
        </span>
      </v-card-title>
      <v-card-subtitle v-if="contactEmail" class="pb-2">
        {{ contactEmail }}
      </v-card-subtitle>
      <v-divider />
      <!-- Eroarea la salvare rămâne inline în dialog (docs/design.md), deasupra zonei derulabile, ca să fie vizibilă
           oricât de jos e derulată lista; 409 Privacy numește listele blocate. -->
      <v-alert v-if="saveError" :type="saveErrorType" density="comfortable" class="ma-4 mb-0" role="alert">
        {{ saveError }}
      </v-alert>

      <v-card-text class="pa-0" style="max-height: 480px;">
        <!-- Loading -->
        <div v-if="loading" class="pa-4">
          <v-skeleton-loader type="list-item-two-line@5" :loading="true" />
        </div>

        <!-- Empty -->
        <v-alert
          v-else-if="!allLists.length"
          type="info"
          density="comfortable"
          class="ma-4"
        >
          {{ $t("ncrm.integrations.mailjet.manageLists.empty") }}
        </v-alert>

        <!-- List with checkboxes -->
        <v-list v-else density="comfortable" class="pa-0">
          <v-list-item
            v-for="list in allLists"
            :key="list.id"
            :title="list.name"
            :subtitle="formatSubtitle(list)"
            @click="toggle(list.id)"
          >
            <template #prepend>
              <v-checkbox-btn
                :model-value="isSelected(list.id)"
                color="primary"
                @click.stop="toggle(list.id)"
              />
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <span class="text-caption text-medium-emphasis">
          {{ $t("ncrm.integrations.mailjet.manageLists.summary", {
            add: addedCount,
            remove: removedCount,
          }) }}
        </span>
        <v-spacer />
        <v-btn variant="text" :disabled="saving" @click="cancel">
          {{ $t("ncrm.integrations.mailjet.manageLists.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="saving"
          :disabled="!hasChanges"
          @click="save"
        >
          {{ $t("ncrm.integrations.mailjet.manageLists.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";

export default {
  name: "NcrmMailjetManageListsDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    accountId: { type: [Number, String], required: true },
    contactId: { type: [Number, String], required: true },
    contactEmail: { type: String, default: "" },
    // Initial list of LIST ids the contact is currently subscribed to.
    // Used to seed the checkbox state without an extra round-trip.
    initialListIds: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue", "saved", "notify"],
  data() {
    return {
      allLists: [],
      // Set<Number> of currently-checked list ids (mutated as user toggles).
      selected: new Set(),
      // Snapshot of selected at open time — used for hasChanges + diff display.
      initialSelected: new Set(),
      loading: false,
      saving: false,
      saveError: "",
      saveErrorType: "error",
    };
  },
  computed: {
    dialogOpen: {
      get() { return this.modelValue; },
      set(value) { this.$emit("update:modelValue", value); },
    },
    hasChanges() {
      if (this.selected.size !== this.initialSelected.size) return true;
      for (const id of this.selected) {
        if (!this.initialSelected.has(id)) return true;
      }
      return false;
    },
    addedCount() {
      let n = 0;
      for (const id of this.selected) {
        if (!this.initialSelected.has(id)) n += 1;
      }
      return n;
    },
    removedCount() {
      let n = 0;
      for (const id of this.initialSelected) {
        if (!this.selected.has(id)) n += 1;
      }
      return n;
    },
  },
  watch: {
    // `immediate: true` because the dialog is mounted lazily via v-if when
    // the parent opens it — meaning modelValue is ALREADY true at mount
    // time and a plain watcher wouldn't see a "transition" to fire on.
    // Without immediate, openDialog() never runs and the user sees the
    // empty-state banner even when there are visible lists in DB.
    modelValue: {
      handler(open) {
        if (open) this.openDialog();
      },
      immediate: true,
    },
  },
  methods: {
    formatSubtitle(list) {
      const parts = [];
      if (list.address) parts.push(list.address);
      if (Number.isFinite(list.subscriber_count)) {
        parts.push(this.$t("ncrm.integrations.mailjet.manageLists.subscribersLabel", { n: list.subscriber_count }));
      }
      return parts.join(" • ");
    },
    isSelected(id) {
      return this.selected.has(id);
    },
    toggle(id) {
      // Mutating a Set isn't reactive in Vue 3 templates — we recreate it
      // so isSelected / hasChanges / addedCount re-evaluate.
      // A change to the selection makes a previous save error stale (e.g. the blocked list was just unchecked).
      this.saveError = "";
      const next = new Set(this.selected);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      this.selected = next;
    },
    async openDialog() {
      this.saveError = "";
      this.loading = true;
      this.allLists = [];
      try {
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/lists/visible`
        );
        this.allLists = Array.isArray(data?.lists) ? data.lists : [];
        // Seed selection from props. Filter against actual visible lists so
        // a previously-subscribed-but-now-hidden list doesn't end up "stuck"
        // in the dialog state.
        const visibleIds = new Set(this.allLists.map((l) => Number(l.id)));
        const seed = new Set(
          this.initialListIds.map((v) => Number(v)).filter((v) => visibleIds.has(v))
        );
        this.selected = seed;
        this.initialSelected = new Set(seed);
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message:
            error?.response?.data?.error ||
            error?.message ||
            this.$t("ncrm.integrations.mailjet.manageLists.errors.load"),
        });
        this.dialogOpen = false;
      } finally {
        this.loading = false;
      }
    },
    cancel() {
      this.dialogOpen = false;
    },
    // GDPR S8 §7: the Privacy guard refuses the whole save; name the blocked lists so the operator can uncheck them.
    describeSaveError(error) {
      const body = error?.response?.data || {};
      if (body.code === "privacy_list_add_blocked") {
        const blocked = new Set((body.blocked_list_ids || []).map(String));
        const names = this.allLists.filter((list) => blocked.has(String(list.id))).map((list) => list.name);
        return { type: "warning", message: this.$t("ncrm.integrations.mailjet.manageLists.errors.privacyBlocked", {
          lists: names.length ? names.join(", ") : [...blocked].join(", "),
        }) };
      }
      if (error?.response?.status === 503) {
        return { type: "error", message: this.$t("ncrm.integrations.mailjet.manageLists.errors.privacyUnavailable") };
      }
      return { type: "error", message: body.error || error?.message || this.$t("ncrm.integrations.mailjet.manageLists.errors.save") };
    },
    async save() {
      if (!this.hasChanges) return;
      this.saveError = "";
      this.saving = true;
      try {
        const { data } = await axios.put(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/contacts/${this.contactId}/lists`,
          { listIds: Array.from(this.selected) }
        );
        const messageKey = data?.ok
          ? "ncrm.integrations.mailjet.manageLists.alerts.success"
          : "ncrm.integrations.mailjet.manageLists.alerts.partial";
        this.$emit("notify", {
          color: data?.ok ? "success" : "warning",
          message: this.$t(messageKey, {
            added: (data?.added || []).length,
            removed: (data?.removed || []).length,
            failed: (data?.failed || []).length,
          }),
        });
        this.$emit("saved", data);
        this.dialogOpen = false;
      } catch (error) {
        const { type, message } = this.describeSaveError(error);
        this.saveErrorType = type;
        this.saveError = message;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
